/* Hero slider — Silk & Sterling. Vanilla JS, no dependencies. */
(function () {
  "use strict";

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");

  class HeroSlider {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll("[data-hs-slide]"));
      this.segs = Array.from(root.querySelectorAll("[data-hs-goto]"));
      this.count = this.slides.length;
      this.index = 0;
      this.busy = false;
      this.speed = parseInt(getComputedStyle(root).getPropertyValue("--hs-speed"), 10) || 1200;
      this.interval = (parseFloat(root.dataset.interval) || 7) * 1000;
      this.autoplayWanted = root.dataset.autoplay === "true" && !reduceMotion.matches;
      this.userPaused = false;   // pause button
      this.holds = new Set();    // temporary reasons to pause: hover, focus, offscreen, hidden, editor
      this.currentEl = root.querySelector("[data-hs-current]");
      this.statusEl = root.querySelector("[data-hs-status]");
      this.toggleBtn = root.querySelector("[data-hs-toggle]");

      root.classList.add("is-first-paint");
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove("is-first-paint")));

      if (this.count < 2) { this.playVideo(this.slides[0]); return; }

      this.bindControls();
      this.bindKeyboard();
      this.bindSwipe();
      this.bindHoldReasons();
      this.bindParallax();
      this.bindEditor();
      this.preload(1);
      this.playVideo(this.slides[0]);
      this.syncPlayState();
    }

    /* ---------- Navigation ---------- */
    go(to, dir) {
      if (this.busy || to === this.index) return;
      to = (to + this.count) % this.count;
      const from = this.index;
      const prev = this.slides[from];
      const next = this.slides[to];
      this.root.dataset.dir = dir || (to > from ? "next" : "prev");
      this.busy = true;
      this.preload(to);
      this.preload(to + 1);

      // restart CSS animations on the incoming slide
      next.classList.remove("is-active", "is-leaving");
      void next.offsetWidth;

      prev.classList.remove("is-active");
      prev.classList.add("is-leaving");
      next.classList.add("is-active");
      this.setInteractive(next, true);
      this.setInteractive(prev, false);
      this.playVideo(next);

      this.index = to;
      this.updateUI();

      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => {
        prev.classList.remove("is-leaving");
        this.pauseVideo(prev);
        this.busy = false;
      }, reduceMotion.matches ? 420 : this.speed);
    }
    next() { this.go(this.index + 1, "next"); }
    prev() { this.go(this.index - 1, "prev"); }

    setInteractive(slide, on) {
      if (on) { slide.removeAttribute("aria-hidden"); slide.inert = false; slide.removeAttribute("inert"); }
      else { slide.setAttribute("aria-hidden", "true"); slide.inert = true; slide.setAttribute("inert", ""); }
      slide.querySelectorAll("a, button").forEach((el) => el.setAttribute("tabindex", on ? "0" : "-1"));
    }

    updateUI() {
      this.segs.forEach((seg, i) => {
        const active = i === this.index;
        seg.classList.toggle("is-active", active);
        seg.classList.toggle("is-done", i < this.index);
        seg.setAttribute("aria-selected", active ? "true" : "false");
        const fill = seg.querySelector(".hs__seg-fill");
        if (active && fill) { fill.style.animation = "none"; void fill.offsetWidth; fill.style.animation = ""; }
      });
      if (this.currentEl) this.currentEl.textContent = String(this.index + 1);
      if (this.statusEl) this.statusEl.textContent = this.slides[this.index].getAttribute("aria-label") || "";
      this.restartTimer();
    }

    /* ---------- Autoplay (timer synced to the progress bar) ---------- */
    isPlaying() { return this.autoplayWanted && !this.userPaused && this.holds.size === 0; }

    syncPlayState() {
      const playing = this.isPlaying();
      this.root.classList.toggle("is-playing", this.autoplayWanted && !this.userPaused);
      this.root.classList.toggle("is-paused", this.autoplayWanted && (this.userPaused || this.holds.size > 0));
      if (playing) this.startTimer(); else this.stopTimer();
    }
    restartTimer() { this.remaining = this.interval; this.syncPlayState(); }
    startTimer() {
      clearTimeout(this.timer);
      if (this.remaining == null) this.remaining = this.interval;
      this.startedAt = performance.now();
      this.timer = setTimeout(() => {
        if (this.busy) { this.remaining = 250; this.startTimer(); return; }
        this.remaining = null; this.next();
      }, this.remaining);
    }
    stopTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
        if (this.startedAt) this.remaining = Math.max(0, this.remaining - (performance.now() - this.startedAt));
      }
    }
    hold(reason, on) {
      if (on) this.holds.add(reason); else this.holds.delete(reason);
      this.syncPlayState();
    }

    /* ---------- Controls ---------- */
    bindControls() {
      const r = this.root;
      r.querySelector("[data-hs-next]")?.addEventListener("click", () => this.next());
      r.querySelector("[data-hs-prev]")?.addEventListener("click", () => this.prev());
      this.segs.forEach((seg) => seg.addEventListener("click", () => {
        const to = parseInt(seg.dataset.hsGoto, 10);
        this.go(to, to > this.index ? "next" : "prev");
      }));
      if (this.toggleBtn) {
        if (!this.autoplayWanted) { this.toggleBtn.hidden = true; }
        this.toggleBtn.addEventListener("click", () => {
          this.userPaused = !this.userPaused;
          this.toggleBtn.setAttribute("aria-pressed", String(this.userPaused));
          this.toggleBtn.setAttribute("aria-label", this.userPaused ? "Play slideshow" : "Pause slideshow");
          this.syncPlayState();
        });
      }
    }

    bindKeyboard() {
      this.root.addEventListener("keydown", (e) => {
        if (e.target.closest("input, textarea, select")) return;
        if (e.key === "ArrowRight") { e.preventDefault(); this.next(); this.focusActiveSeg(e); }
        if (e.key === "ArrowLeft") { e.preventDefault(); this.prev(); this.focusActiveSeg(e); }
        if (e.key === "Home") { e.preventDefault(); this.go(0, "prev"); this.focusActiveSeg(e); }
        if (e.key === "End") { e.preventDefault(); this.go(this.count - 1, "next"); this.focusActiveSeg(e); }
      });
    }
    focusActiveSeg(e) {
      if (e.target.matches("[data-hs-goto]")) this.segs[this.index]?.focus();
    }

    bindSwipe() {
      const vp = this.root.querySelector("[data-hs-viewport]");
      let x0 = null, y0 = 0, t0 = 0, moved = false;
      vp.addEventListener("pointerdown", (e) => {
        if (e.button !== 0) return;
        x0 = e.clientX; y0 = e.clientY; t0 = performance.now(); moved = false;
      });
      vp.addEventListener("pointermove", (e) => {
        if (x0 === null) return;
        if (Math.abs(e.clientX - x0) > 8 && Math.abs(e.clientX - x0) > Math.abs(e.clientY - y0)) {
          moved = true; this.root.classList.add("is-dragging");
        }
      });
      const end = (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0, dt = performance.now() - t0;
        this.root.classList.remove("is-dragging");
        if (moved && (Math.abs(dx) > 60 || (Math.abs(dx) > 25 && dt < 250))) {
          dx < 0 ? this.next() : this.prev();
        }
        x0 = null;
      };
      vp.addEventListener("pointerup", end);
      vp.addEventListener("pointercancel", () => { x0 = null; this.root.classList.remove("is-dragging"); });
      // a drag must not also count as a click on a slide link
      vp.addEventListener("click", (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      vp.addEventListener("dragstart", (e) => e.preventDefault());
    }

    bindHoldReasons() {
      const r = this.root;
      if (r.dataset.pauseHover === "true") {
        r.addEventListener("mouseenter", () => this.hold("hover", true));
        r.addEventListener("mouseleave", () => this.hold("hover", false));
      }
      // WAI-ARIA carousel pattern: keyboard focus inside stops rotation (mouse clicks don't)
      r.addEventListener("focusin", (e) => {
        let kb = true;
        try { kb = e.target.matches(":focus-visible"); } catch (err) {}
        if (kb) this.hold("focus", true);
      });
      r.addEventListener("focusout", (e) => { if (!r.contains(e.relatedTarget)) this.hold("focus", false); });
      document.addEventListener("visibilitychange", () => this.hold("hidden", document.hidden));
      if ("IntersectionObserver" in window) {
        new IntersectionObserver(([entry]) => {
          this.hold("offscreen", !entry.isIntersecting);
          const v = this.slides[this.index];
          entry.isIntersecting ? this.playVideo(v) : this.pauseVideo(v);
        }, { threshold: 0.25 }).observe(r);
      }
      reduceMotion.addEventListener?.("change", () => {
        this.autoplayWanted = this.root.dataset.autoplay === "true" && !reduceMotion.matches;
        this.syncPlayState();
      });
    }

    /* Subtle depth: the image drifts a few pixels against the pointer */
    bindParallax() {
      if (this.root.dataset.parallax !== "true" || !finePointer.matches || reduceMotion.matches) return;
      let raf = 0;
      this.root.addEventListener("pointermove", (e) => {
        if (e.pointerType !== "mouse") return;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const rect = this.root.getBoundingClientRect();
          const nx = (e.clientX - rect.left) / rect.width - 0.5;
          const ny = (e.clientY - rect.top) / rect.height - 0.5;
          this.root.style.setProperty("--hs-px", (-nx * 14).toFixed(2) + "px");
          this.root.style.setProperty("--hs-py", (-ny * 10).toFixed(2) + "px");
        });
      });
      this.root.addEventListener("pointerleave", () => {
        this.root.style.setProperty("--hs-px", "0px");
        this.root.style.setProperty("--hs-py", "0px");
      });
    }

    /* Theme editor: selecting a slide block shows it and holds rotation */
    bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener("shopify:block:select", (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.hold("editor", true); this.busy = false; this.go(i); }
      });
      document.addEventListener("shopify:block:deselect", () => this.hold("editor", false));
    }

    /* ---------- Media ---------- */
    preload(i) {
      const s = this.slides[(i + this.count) % this.count];
      s?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = "eager"; });
    }
    playVideo(slide) {
      const v = slide && slide.querySelector("video");
      if (v && !reduceMotion.matches) { v.muted = true; v.play().catch(() => {}); }
    }
    pauseVideo(slide) {
      const v = slide && slide.querySelector("video");
      if (v) v.pause();
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll("[data-hero-slider]").forEach((el) => {
      if (el.__hs) return;
      el.__hs = new HeroSlider(el);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
  document.addEventListener("shopify:section:load", (e) => init(e.target));
})();
